/**
 * THE PROGRAM BOARD'S RULES — the pager, the grid, and the week edits, as pure functions.
 *
 * `docs/PROGRAMS-DESIGN.md` §4: two week rows visible at a time, each seven cells `MON … SUN`,
 * a `‹ Weeks a–b of N ›` pager, `Add week`, and a week's `⋯` offering `Duplicate week` and
 * `Remove week`.
 *
 * 🔴 EVERY ONE OF THESE IS A PLAN, NOT A WRITE. The screen executes what these return; nothing
 * here touches a client. That is the same split `assignProgram.ts` carries and for the same
 * reason: `Remove week` renumbers every week after it, and a renumbering is exactly the kind of
 * rule that is right for the middle case and wrong at either end.
 */

/** 1 = Monday, matching the phone's Monday-first calendar. Never the locale's `startOfWeek`. */
export const WEEKDAYS = [
  { weekday: 1, short: "MON", long: "Monday" },
  { weekday: 2, short: "TUE", long: "Tuesday" },
  { weekday: 3, short: "WED", long: "Wednesday" },
  { weekday: 4, short: "THU", long: "Thursday" },
  { weekday: 5, short: "FRI", long: "Friday" },
  { weekday: 6, short: "SAT", long: "Saturday" },
  { weekday: 7, short: "SUN", long: "Sunday" },
] as const;

/** One `coach_program_days` row as the board holds it, with its template's name resolved. */
export type BoardDay = {
  id: string;
  weekIndex: number;
  weekday: number;
  position: number;
  workoutId: string;
  /** The TEMPLATE's name. Null when the library row could not be read — said, never guessed. */
  workoutName: string | null;
  note: string | null;
};

/** F15 3.9 — one `coach_program_weeks` row: a week that is a DELOAD and/or AUTO (derived). No row = a written week. */
export type BoardWeek = { weekIndex: number; isDeload: boolean; derived: boolean };

/** How many weeks the board draws. `max(week_index)` over the day rows AND the week rows (3.9), floored at 1 so a new block
 *  has a week. */
export function boardWeeks(days: readonly BoardDay[], weekRows: readonly BoardWeek[] = []): number {
  let max = 0;
  for (const d of days) if (d.weekIndex > max) max = d.weekIndex;
  for (const w of weekRows) if (w.weekIndex > max) max = w.weekIndex;
  /* ⚠️ FLOORED AT ONE, AND THE LIST'S `weekCount` IS NOT. They answer different questions: the
     list says how long a block IS (0 is a true and useful answer — "nothing planned yet"), and
     the board has to DRAW somewhere for the first template to be dropped. A board with zero week
     rows is a screen with no way to start. */
  return Math.max(max, 1);
}

/**
 * The pager's window — TWO weeks, exactly theirs.
 *
 * ⚠️ CLAMPED AT BOTH ENDS AND THE LAST PAGE IS NOT SHORTENED WHEN IT CAN BE FULL. With five
 * weeks, paging to the end shows weeks 4–5, not 5 alone: a half-empty last page is the shape a
 * coach reads as "the block ends here" one week early. With ONE week it shows one, because there
 * is nothing to pad with.
 */
export function pagerWindow(weeks: number, firstWanted: number): { first: number; last: number } {
  const span = Math.min(2, Math.max(weeks, 1));
  const maxFirst = Math.max(1, weeks - span + 1);
  const first = Math.min(Math.max(firstWanted, 1), maxFirst);
  return { first, last: Math.min(first + span - 1, Math.max(weeks, 1)) };
}

/** `Weeks 3–4 of 6`, or `Week 1 of 1` when there is only one to name. */
export function pagerLabel(win: { first: number; last: number }, weeks: number): string {
  const total = Math.max(weeks, 1);
  return win.first === win.last
    ? `Week ${String(win.first)} of ${String(total)}`
    : `Weeks ${String(win.first)}–${String(win.last)} of ${String(total)}`;
}

/** The day rows in one cell, in `position` order. */
export function cellDays(
  days: readonly BoardDay[], weekIndex: number, weekday: number,
): BoardDay[] {
  /* ⚠️ SORTED HERE AND NOT TRUSTED TO ARRIVE SORTED — a PostgREST read comes back unordered
     (b4-2/3/10), and the fixtures shuffle on purpose so a dropped sort is a red. */
  return days
    .filter((d) => d.weekIndex === weekIndex && d.weekday === weekday)
    .sort((a, b) => a.position - b.position);
}

/** What a card prints. A template whose name could not be read SAYS SO. */
export function cardTitle(day: BoardDay): string {
  return day.workoutName ?? "This template could not be read";
}

/* ── The week edits, as plans ──────────────────────────────────────────────────────────────── */

/** A row to insert, with no id — the database mints it. */
export type DayInsert = {
  weekIndex: number; weekday: number; position: number; workoutId: string; note: string | null;
};

/** What `Add week` does: nothing but move the pager. A week with no rows IS a rest week. */
export function weekAfterAdd(weeks: number): number {
  return weeks + 1;
}

/**
 * `Duplicate week` — copy a week's rows to a NEW LAST week.
 *
 * ⚠️ TO THE END, NOT BESIDE ITSELF, and that is a decision rather than a shortcut. Inserting a
 * copy at `k + 1` would renumber every week after it — the same renumbering `Remove week` does,
 * run in the opposite direction, on a block a client may already be part-way through. §4 says
 * *"copies its day rows to a new last week"* and the coach can reorder by moving cards.
 */
export function duplicateWeekPlan(
  days: readonly BoardDay[], weeks: number, sourceWeek: number,
): DayInsert[] {
  return days
    .filter((d) => d.weekIndex === sourceWeek)
    .sort((a, b) => a.weekday - b.weekday || a.position - b.position)
    .map((d) => ({
      weekIndex: weeks + 1, weekday: d.weekday, position: d.position,
      workoutId: d.workoutId, note: d.note,
    }));
}

/** What `Remove week` does: delete these ids, then shift these weeks down by one. */
export type RemoveWeekPlan = {
  deleteIds: string[];
  /** The week indexes that must each lose one, LOWEST FIRST — see the note below. */
  shiftWeeks: number[];
};

/**
 * ⚠️ LOWEST FIRST, AND THIS IS THE WHOLE REASON THE PLAN IS A LIST RATHER THAN ONE STATEMENT.
 * PostgREST cannot express `column = column - 1`, so the board issues one update per week. The
 * weeks move DOWN, so each destination must be empty when it is written: week `k` was just deleted,
 * so `k+1` goes onto it first, which empties `k+1` for `k+2`, and so on. 🔴 THIS SAID "HIGHEST
 * FIRST" UNTIL 2026-11-01 and was WRONG: highest first writes `k+2` onto `k+1` while `k+1` still
 * holds its rows — MEASURED live, rolled back (`tools/phone-asks-b-20261101/rehearse.sh desc`):
 * 23505 on the example coach's 4-week program, whose weeks repeat cell for cell. Latest-first is
 * right only for a shift FORWARD (`shiftBlock.ts`), which is where the order was borrowed from.
 */
export function removeWeekPlan(days: readonly BoardDay[], weeks: number, week: number): RemoveWeekPlan {
  const deleteIds = days.filter((d) => d.weekIndex === week).map((d) => d.id);
  const shiftWeeks: number[] = [];
  for (let w = week + 1; w <= weeks; w += 1) shiftWeeks.push(w);
  return { deleteIds, shiftWeeks };
}

/**
 * The position a new card takes in a cell: after everything already there.
 *
 * ⚠️ `length` IS NOT THE ANSWER. Positions are dense per day by unique key (§2.2), but a cell
 * read mid-edit can be missing a row, and `max + 1` collides with nothing either way while
 * `length` would collide with a `[0, 2]` cell.
 */
export function nextPosition(days: readonly BoardDay[], weekIndex: number, weekday: number): number {
  const here = cellDays(days, weekIndex, weekday);
  return here.reduce((max, d) => Math.max(max, d.position + 1), 0);
}

/**
 * May this template be dropped into this cell?
 *
 * ⚠️ THE UNIQUE KEY `(program_id, week_index, weekday, workout_id)` MAKES A SECOND COPY OF ONE
 * TEMPLATE ON ONE DAY UNREPRESENTABLE (§2.2) — because the DATABASE would refuse the second
 * assignment with 23505 anyway, and a plan that cannot be assigned must be impossible to draw
 * rather than discovered at the click. So the picker's answer is computed, not caught.
 */
export function canAddToCell(
  days: readonly BoardDay[], weekIndex: number, weekday: number, workoutId: string,
): boolean {
  return !cellDays(days, weekIndex, weekday).some((d) => d.workoutId === workoutId);
}

/* ── W137 cut 3 (R8, R6b) — THE BOARD'S DAY DRAG AND COPY ────────────────────────────────────────────────────────────────
 * The board draws the SAME day column and the SAME drag as the client Week (his "needs to look same"); what a drop MEANS
 * here is a `coach_program_days` row changing (week_index, weekday, position). The arithmetic of WHERE is `dayDrag`'s; this
 * part turns its plan into row writes this table accepts. */

/** The board's day key — one string per (week, weekday) cell, so the shared drag can address a cell like a calendar day. */
export function boardDayKey(week: number, weekday: number): string {
  return `w${String(week)}-d${String(weekday)}`;
}
export function parseBoardDayKey(key: string): { week: number; weekday: number } | null {
  const m = /^w(\d+)-d([1-7])$/.exec(key);
  return m === null ? null : { week: Number(m[1]), weekday: Number(m[2]) };
}

/** The visible weeks' cells → the drag's arrangement (each cell's day ids in position order). */
export function boardArrangement(days: readonly BoardDay[], weeks: readonly number[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const w of weeks) for (const wd of WEEKDAYS) out[boardDayKey(w, wd.weekday)] = cellDays(days, w, wd.weekday).map((d) => d.id);
  return out;
}

/** One update of one row. */
export type BoardWrite = { id: string; values: { week_index?: number; weekday?: number; position: number } };

/**
 * 🔴 THE WRITES OF ONE DROP, IN AN ORDER THE TABLE ACCEPTS. `(program_id, week_index, weekday, position)` is UNIQUE and not
 * deferrable (MEASURED live 2026-10-02), so writing the final positions straight would collide the moment two rows trade
 * places. Three phases: (1) every row whose order changes steps aside to a temporary position (100 + its index in the
 * list — the CHECK only asks ≥ 0, and no live cell holds 100 rows); (2) the moved row goes to its new cell at its slot;
 * (3) the rows that stepped aside take their final positions. A row in both lists is written once per phase it needs.
 */
export function boardMoveWrites(
  plan: { id: string; moved: { from: string; to: string } | null; positions: readonly { id: string; position: number }[] },
  /** Each row's stored position: the moved row keeps it when its new slot equals it (the plan then lists no position). */
  stored: Readonly<Record<string, number>>,
): BoardWrite[] | null {
  const writes: BoardWrite[] = [];
  const others = plan.positions.filter((p) => p.id !== plan.id);
  others.forEach((p, i) => writes.push({ id: p.id, values: { position: 100 + i } }));
  let movedTo: { week: number; weekday: number; position: number } | null = null;
  if (plan.moved !== null) {
    const cell = parseBoardDayKey(plan.moved.to);
    if (cell === null) return null;
    const position = plan.positions.find((p) => p.id === plan.id)?.position ?? stored[plan.id];
    if (position === undefined) return null;
    movedTo = { ...cell, position };
  } else {
    const self = plan.positions.find((p) => p.id === plan.id);
    if (self !== undefined) writes.push({ id: plan.id, values: { position: 100 + others.length } });
  }
  if (movedTo !== null) writes.push({ id: plan.id, values: { week_index: movedTo.week, weekday: movedTo.weekday, position: movedTo.position } });
  for (const p of others) writes.push({ id: p.id, values: { position: p.position } });
  if (plan.moved === null) {
    const self = plan.positions.find((p) => p.id === plan.id);
    if (self !== undefined) writes.push({ id: plan.id, values: { position: self.position } });
  }
  return writes;
}

/** May the dropped card land in that cell? Not when the cell already holds THIS template (the one-template-per-day key). */
export function canMoveToCell(days: readonly BoardDay[], id: string, week: number, weekday: number): boolean {
  const day = days.find((d) => d.id === id);
  if (day === undefined) return false;
  return !cellDays(days, week, weekday).some((d) => d.id !== id && d.workoutId === day.workoutId);
}

/** The words the board says about a refused move or copy — the picker's own (`Already on this day`). */
export const ALREADY_ON_THIS_DAY = "Already on this day.";

/* ── THE PROGRAM ITSELF: RENAME AND ARCHIVE (2026-11-02, both boards) ───────────────────────────────────────────────────────
 * A name obeys the table's CHECK (`btrim(name) <> '' and length(name) <= 120`), said BEFORE the round trip — `+ New` and
 * Rename ask the one function. Archive never deletes: `archived_at` takes the program off the Library's list; the clients it
 * was given keep their weeks (copy-on-assign), and the weekly writer still reads its block (no `archived_at` filter there). */

/** Why a name cannot be saved, or null. The trimmed name is what is written. */
export function nameProblem(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === "") return "Give the program a name.";
  if (trimmed.length > 120) return "That name is longer than 120 characters.";
  return null;
}
/** The archive's confirm — what it does, and what it leaves alone. */
export const ARCHIVE_SAID = "It leaves your Library. Clients it was given keep their weeks.";

/* ── F15 STEP 3.9 — THE WEEK'S DELOAD MARK, AUTO (DERIVED) OR WRITTEN, AND THE PLAN LINE (DECISIONS 2026-10-31) ─────────────
 * A week row exists only when it says something: `is_deload` and/or `derived`. A (false, false) row is deleted, never kept —
 * it would read as a written week anyway, and "no row = written" is what keeps every program from before 3.9 byte-identical. */

export const AUTO_WORD = "Auto";
export const DELOAD_WORD = "Deload";

/** What a week-row edit writes: an upsert of the whole row, or the row's delete when it would say nothing. */
export type WeekRowWrite = { kind: "upsert"; row: BoardWeek } | { kind: "delete"; weekIndex: number };

export function weekRowOf(weekRows: readonly BoardWeek[], week: number): BoardWeek {
  return weekRows.find((w) => w.weekIndex === week) ?? { weekIndex: week, isDeload: false, derived: false };
}
function rowWrite(row: BoardWeek): WeekRowWrite {
  return row.isDeload || row.derived ? { kind: "upsert", row } : { kind: "delete", weekIndex: row.weekIndex };
}

/** The deload switch on a week's tab: on ↔ off, the week's written/auto state kept. */
export function toggleDeload(weekRows: readonly BoardWeek[], week: number): WeekRowWrite {
  const r = weekRowOf(weekRows, week);
  return rowWrite({ ...r, isDeload: !r.isDeload });
}

/** `Add week` (3.9): the new last week is AUTO — nobody types 52 weeks; `Edit` makes it written. */
export function addAutoWeek(weeks: number): WeekRowWrite {
  return { kind: "upsert", row: { weekIndex: weeks + 1, isDeload: false, derived: true } };
}

/** The weeks that are auto, as a set. Week 1 is never auto (the table's CHECK) — a row saying so is read as written. */
export function autoWeeks(weekRows: readonly BoardWeek[]): Set<number> {
  return new Set(weekRows.filter((w) => w.derived && w.weekIndex > 1).map((w) => w.weekIndex));
}

/** `Edit` on an auto week: copy its source week's day rows in (the layout it was drawing), then the row stops being auto. */
export function makeWrittenPlan(days: readonly BoardDay[], weekRows: readonly BoardWeek[], week: number):
  { inserts: DayInsert[]; row: WeekRowWrite } {
  const auto = autoWeeks(weekRows);
  let from = week;
  while (from > 1 && auto.has(from)) from -= 1;
  const inserts = days.filter((d) => d.weekIndex === from)
    .sort((a, b) => a.weekday - b.weekday || a.position - b.position)
    .map((d) => ({ weekIndex: week, weekday: d.weekday, position: d.position, workoutId: d.workoutId, note: d.note }));
  return { inserts, row: rowWrite({ ...weekRowOf(weekRows, week), derived: false }) };
}

/** `Make auto` on a written week (never week 1): its day rows go, the row says auto. The board confirms first. */
export function makeAutoPlan(days: readonly BoardDay[], weekRows: readonly BoardWeek[], week: number):
  { deleteIds: string[]; row: WeekRowWrite } | null {
  if (week <= 1) return null;
  return { deleteIds: days.filter((d) => d.weekIndex === week).map((d) => d.id),
           row: rowWrite({ ...weekRowOf(weekRows, week), derived: true }) };
}

/** `Remove week` (3.9): the week's own row goes too, and every later week row shifts down LOWEST FIRST, like the days (the
 *  primary key (program_id, week_index) refuses a row written onto one not yet moved). */
export function removeWeekRowsPlan(weekRows: readonly BoardWeek[], weeks: number, week: number):
  { deleteWeek: boolean; shiftWeeks: number[] } {
  const shiftWeeks: number[] = [];
  for (let w = week + 1; w <= weeks; w += 1) if (weekRows.some((r) => r.weekIndex === w)) shiftWeeks.push(w);
  return { deleteWeek: weekRows.some((r) => r.weekIndex === week), shiftWeeks };
}

/** `Duplicate week` (3.9): the copy at the new last week carries the source's row (its deload mark, its auto state). */
export function duplicateWeekRow(weekRows: readonly BoardWeek[], weeks: number, sourceWeek: number): BoardWeek | null {
  const r = weekRows.find((w) => w.weekIndex === sourceWeek);
  return r === undefined ? null : { ...r, weekIndex: weeks + 1 };
}

/**
 * THE PROGRAM'S PLAN LINE — the one place for length, how many are written and the board's marked deload weeks (his 10-31
 * word (4)). F15 4.2: the Deload choice is the block's own pick under it, never said twice. 11-02: an AUTO block's marks are
 * not read (kept, never cleared), so they are not named.
 *   `12 weeks · 3 written · Deload weeks 4, 8, 12` · `4 weeks · 1 written`
 */
export function planLine(weeks: number, weekRows: readonly BoardWeek[], autoDeload = false): string {
  const auto = autoWeeks(weekRows);
  const written = weeks - [...auto].filter((w) => w <= weeks).length;
  const marked = autoDeload ? []
    : weekRows.filter((w) => w.isDeload && w.weekIndex <= weeks).map((w) => w.weekIndex).sort((a, b) => a - b);
  const head = `${String(weeks)} ${weeks === 1 ? "week" : "weeks"} · ${String(written)} written`;
  return marked.length === 0 ? head : `${head} · ${marked.length === 1 ? "Deload week" : "Deload weeks"} ${marked.join(", ")}`;
}
